/**
 * Single source of truth for connector types exposed in Admin UI and
 * aligned with API validation in adminConnectorsController.
 */

export const connectorPlatforms = ["hubspot", "intercom", "zendesk"] as const;

export type ConnectorPlatform = (typeof connectorPlatforms)[number];

export const inboundAuthTypes = ["static_bearer", "api_key"] as const;

export type InboundAuthType = (typeof inboundAuthTypes)[number];

export const outboundAuthTypes = ["none", "bearer", "api_key_header"] as const;

export type OutboundAuthType = (typeof outboundAuthTypes)[number];

export type ConnectorFeatureKey =
  | "feature_connector_hubspot"
  | "feature_connector_intercom"
  | "feature_connector_zendesk";

export type ConnectorCatalogEntry = {
  platform: ConnectorPlatform;
  displayName: string;
  description: string;
  /** Absolute documentation URL for deep links from product surfaces */
  docsPath: string;
  /** Stable key for UI icons / theming (no asset URLs in shared) */
  iconKey: string;
  /** Entitlement key that gates this connector in OrganizationEntitlements */
  featureKey: ConnectorFeatureKey;
  supports: {
    inboundAuthTypes: readonly InboundAuthType[];
    outboundAuthTypes: readonly OutboundAuthType[];
  };
  defaultConfig: {
    label: string;
    inbound_auth_type: InboundAuthType;
    outbound_auth_type: OutboundAuthType;
    outbound_base_url: string;
    domain_allowlist: readonly string[];
  };
};

export const connectorCatalog: readonly ConnectorCatalogEntry[] = [
  {
    platform: "hubspot",
    displayName: "HubSpot",
    description:
      "Map HubSpot workflow or webhook payloads to Veritrellis actions; relay results to your backend after allow or approval.",
    docsPath: "https://docs.veritrellis.ai/connectors/hubspot",
    iconKey: "hubspot",
    featureKey: "feature_connector_hubspot",
    supports: {
      inboundAuthTypes: ["static_bearer", "api_key"],
      outboundAuthTypes: ["none", "bearer", "api_key_header"]
    },
    defaultConfig: {
      label: "HubSpot",
      inbound_auth_type: "api_key",
      outbound_auth_type: "none",
      outbound_base_url: "https://api.example.com",
      domain_allowlist: []
    }
  },
  {
    platform: "intercom",
    displayName: "Intercom",
    description:
      "Fin or custom actions: procedure names map to platform actions; outbound relay after policy decision.",
    docsPath: "https://docs.veritrellis.ai/connectors/intercom",
    iconKey: "intercom",
    featureKey: "feature_connector_intercom",
    supports: {
      inboundAuthTypes: ["static_bearer", "api_key"],
      outboundAuthTypes: ["none", "bearer", "api_key_header"]
    },
    defaultConfig: {
      label: "Intercom",
      inbound_auth_type: "api_key",
      outbound_auth_type: "none",
      outbound_base_url: "https://api.example.com",
      domain_allowlist: []
    }
  },
  {
    platform: "zendesk",
    displayName: "Zendesk",
    description:
      "AI agent custom actions: stable action names and optional metadata hints for audit-friendly logs.",
    docsPath: "https://docs.veritrellis.ai/connectors/zendesk",
    iconKey: "zendesk",
    featureKey: "feature_connector_zendesk",
    supports: {
      inboundAuthTypes: ["static_bearer", "api_key"],
      outboundAuthTypes: ["none", "bearer", "api_key_header"]
    },
    defaultConfig: {
      label: "Zendesk",
      inbound_auth_type: "api_key",
      outbound_auth_type: "none",
      outbound_base_url: "https://api.example.com",
      domain_allowlist: []
    }
  }
] as const;

const catalogByPlatform: ReadonlyMap<ConnectorPlatform, ConnectorCatalogEntry> = new Map(
  connectorCatalog.map((e) => [e.platform, e])
);

export const getConnectorCatalogEntry = (
  platform: ConnectorPlatform
): ConnectorCatalogEntry | undefined => {
  return catalogByPlatform.get(platform);
};

export const isConnectorPlatform = (value: string): value is ConnectorPlatform => {
  return (connectorPlatforms as readonly string[]).includes(value);
};
